import {
  Box,
  Button,
  Grid,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
} from "@chakra-ui/react";
import { GoBell, GoDotFill, GoRocket } from "react-icons/go";
import { useMemo } from "react";
import { useNavigate } from "react-router";
import { TiFlash } from "react-icons/ti";
import dayjs from "../../helpers/dayJs";
import isURL from "validator/lib/isURL";
import useNotifications from "../../hooks/data/useNotifications";

function NotificationsUI() {
  const navigate = useNavigate();
  const {
    notifications,
    isLoading,
    markAsRead,
    markAllAsRead,
    getUnreadCount,
  } = useNotifications();

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.isRead).length,
    [notifications]
  );

  function handleItemClick(n) {
    markAsRead(n.id);
    if (n.customPath) {
      if (isURL(n.customPath))
        window.open(n.customPath, `_blank`).focus();
      else navigate(n.customPath);
    }
  }

  return (
    <Menu placement="bottom-end">
      <MenuButton
        as={Button}
        aria-label="Notifications"
        color="gray.400"
        bg="transparent"
        borderRadius={"full"}
        h="45px"
        w="45px"
        variant={"nav"}
        size="lg"
        minW="0"
        p="0.5"
        display="flex"
        alignItems="center"
        justifyContent="center"
        pos="relative"
        leftIcon={
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            w="100%"
            h="100%"
            pointerEvents="none"
          >
            <GoBell size={17} />
          </Box>
        }
        rightIcon={
          getUnreadCount() > 0 && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-30%, -35%)"
              w="100%"
              h="100%"
              pointerEvents="none"
              color="red.400"
            >
              <GoDotFill />
            </Box>
          )
        }
      ></MenuButton>
      <MenuList color={"white"} p="1" w="350px" maxH='500px' overflowX={'scroll'}>
        <Text fontSize={"md"} fontWeight={"semibold"} px="3" py="1.5">
          Notifications
        </Text>
        <VStack w='full' h='max-content'>
          {isLoading && notifications.length === 0 && (
            <MenuItem>
              <Text fontSize="sm" color="gray.300">
                Loading notifications...
              </Text>
            </MenuItem>
          )}

          {!isLoading && notifications.length === 0 && (
            <MenuItem>
              <Text fontSize="sm" color="gray.300">
                No notifications yet
              </Text>
            </MenuItem>
          )}

          {notifications.map((n, index) => (
            <MenuItem
              key={n.id}
              h={"max-content"}
              onClick={() => handleItemClick(n)}
              borderBottom={
                index < notifications?.length - 1 ? "1px solid" : "none"
              }
              borderBottomColor={"gray.700"}
            >
              <Grid
                gridTemplateRows={"repeat(2, auto)"}
                columnGap={"3"}
                py="1"
                rowGap={"1"}
              >
                <HStack ml={!n.isRead && "-1"} spacing={"1"}>
                  {!n.isRead && (
                    <Box color="red.400">
                      <GoDotFill />
                    </Box>
                  )}
                  <Text fontSize={"xs"} color={"gray.400"}>
                    {dayjs(n?.createdOn?.toDate()).calendar()}
                  </Text>
                </HStack>
                <VStack align={"flex-start"} spacing={"0.5"}>
                  <Text>{n.body}</Text>
                  {n.points && (
                    <HStack
                      borderRadius={"lg"}
                      bg="yellow.400"
                      color="yellow.900"
                      fontSize="12px"
                      fontWeight="semibold"
                      pl="1"
                      pr="2.5"
                      py="0"
                      justify={"flex-start"}
                      spacing={"0"}
                      w="max-content"
                      mt="2"
                    >
                      <TiFlash />
                      <Text>+{n.points}</Text>
                    </HStack>
                  )}
                </VStack>
              </Grid>
            </MenuItem>
          ))}
        </VStack>
      </MenuList>
    </Menu>
  );
}

export default NotificationsUI;
